import React, { useRef, useState } from 'react';

const PostmanImporterControls = ({ config, onChange }) => {
  const fileInputRef = useRef(null);
  const [pastedJson, setPastedJson] = useState('');

  const handleChange = (key, value) => {
    onChange({ [key]: value });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.json')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          handleChange('collectionFile', json);
        } catch (error) {
          alert('Invalid JSON file: ' + error.message);
        }
      };
      reader.readAsText(file);
    } else {
      alert('Please upload a JSON file (Postman collection export)');
    }
  };

  const handleLoadJson = () => {
    try {
      const json = JSON.parse(pastedJson);
      handleChange('collectionFile', json);
    } catch (error) {
      alert('Invalid JSON: ' + error.message);
    }
  };

  const handleClearJson = () => {
    setPastedJson('');
    handleChange('collectionFile', null);
  };

  // Analyze collection for display
  const analyzeCollection = () => {
    if (!config.collectionFile) return null;
    
    try {
      const collection = config.collectionFile;
      const items = collection.item || [];
      let endpointCount = 0;
      const methods = {};
      const authTypes = new Set();
      let hasFileUploads = false;
      
      const countEndpoints = (items) => {
        items.forEach(item => {
          if (item.request) {
            endpointCount++;
            const method = (item.request.method || 'GET').toUpperCase();
            methods[method] = (methods[method] || 0) + 1;
            
            // Check auth
            const auth = item.request.auth || collection.auth || {};
            if (auth.type && auth.type !== 'noauth') {
              authTypes.add(auth.type);
            }
            
            // Check for file uploads
            const body = item.request.body;
            if (body && (body.mode === 'formdata' || body.mode === 'file')) {
              const formdata = body.formdata || [];
              if (formdata.some(item => item.type === 'file') || body.mode === 'file') {
                hasFileUploads = true;
              }
            }
          }
          if (item.item) {
            countEndpoints(item.item);
          }
        });
      };
      
      countEndpoints(items);
      
      return {
        endpointCount,
        methods,
        authTypes: Array.from(authTypes),
        hasFileUploads
      };
    } catch (e) {
      return null;
    }
  };

  const analysis = analyzeCollection();

  return (
    <div className="space-y-6">
      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Upload Postman Collection
        </label>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-gradient-primary bg-gradient-primary-hover rounded text-white text-sm"
          >
            Choose File
          </button>
        </div>

        {/* Manual Paste (no clipboard permission) */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Paste Postman Collection JSON
          </label>
          <textarea
            value={pastedJson}
            onChange={(e) => setPastedJson(e.target.value)}
            rows={6}
            placeholder='Paste exported Postman collection JSON (v2.1) here...'
            className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm font-mono"
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleLoadJson}
              disabled={!pastedJson.trim()}
              className="px-4 py-2 bg-gray-900 dark:bg-gray-900 hover:opacity-90 rounded text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Load JSON
            </button>
            <button
              onClick={handleClearJson}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300 text-sm"
            >
              Clear
            </button>
          </div>
        </div>

        {config.collectionFile && (
          <div className="mt-2">
            <p className="text-xs text-green-700 dark:text-green-400">
              ✓ Collection loaded
            </p>
            {analysis && (
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                Found {analysis.endpointCount} endpoint{analysis.endpointCount !== 1 ? 's' : ''}
                {analysis.hasFileUploads && ' • Includes file uploads'}
                {analysis.authTypes.length > 0 && ` • Auth: ${analysis.authTypes.join(', ')}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pattern Selection */}
      {config.collectionFile && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Service Pattern
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { value: 'singleton', label: 'Singleton' },
              { value: 'generic', label: 'Generic Base Class' },
              { value: 'repository', label: 'Repository Pattern' },
              { value: 'functional', label: 'Functional Helpers' },
              { value: 'interceptor', label: 'Interceptor-Based' },
            ].map((pattern) => (
              <button
                key={pattern.value}
                onClick={() => handleChange('pattern', pattern.value)}
                className={`
                  px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                  ${
                    (config.pattern || 'singleton') === pattern.value
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {pattern.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Code Generation Options */}
      {config.collectionFile && (
        <div className="space-y-3">
          <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
            Generated Swift Networking API Options
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useAsyncAwait ?? true}
              onChange={(e) => handleChange('useAsyncAwait', e.target.checked)}
              className="rounded"
            />
            Include async/await API
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.includeCombineWrapper ?? true}
              onChange={(e) => handleChange('includeCombineWrapper', e.target.checked)}
              className="rounded"
            />
            Include Combine publisher wrapper
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Request Timeout (seconds)
            </label>
            <input
              type="number"
              min="1"
              max="300"
              value={config.timeout || 30}
              onChange={(e) => handleChange('timeout', parseInt(e.target.value) || 30)}
              className="w-full px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.enableRetry || false}
              onChange={(e) => handleChange('enableRetry', e.target.checked)}
              className="rounded"
            />
            Enable Retry with Exponential Backoff
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.enableLogging || false}
              onChange={(e) => handleChange('enableLogging', e.target.checked)}
              className="rounded"
            />
            Enable Request/Response Logging
          </label>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-4">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <strong>How to export from Postman:</strong>
        </p>
        <ol className="text-sm text-blue-800 dark:text-blue-300 mt-2 ml-4 list-decimal space-y-1">
          <li>Open your Postman collection</li>
          <li>Click the three dots (⋯) next to the collection name</li>
          <li>Select "Export"</li>
          <li>Choose "Collection v2.1" format</li>
          <li>Upload the exported JSON file here</li>
        </ol>
        {config.collectionFile && (
          <p className="text-sm text-blue-800 dark:text-blue-300 mt-3">
            <strong>Features:</strong> All endpoints will be generated in a single constants file with support for HTTP methods, headers, auth tokens, query strings, and file uploads.
          </p>
        )}
      </div>
    </div>
  );
};

export default PostmanImporterControls;

